import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Wallet,
  Clock,
  CheckCircle2,
  Building2,
  ArrowDownToLine,
  Shield,
  AlertCircle,
  Loader2,
  XCircle,
  ChevronRight,
  Search,
  Download,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { MerchantShell } from "@/components/merchant/merchant-shell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Money, Riyal } from "@/components/ui/riyal";

export const Route = createFileRoute("/merchant/payouts")({
  head: () => ({
    meta: [
      { title: "Payouts — Spayzo Merchant" },
      { name: "description", content: "Withdraw your earnings and track payout history." },
    ],
  }),
  component: PayoutsPage,
});

type PStatus = "processing" | "completed" | "failed";
const statusKeys: Record<PStatus, { key: string; icon: React.ComponentType<{ className?: string }>; cls: string }> = {
  processing: { key: "merchantPayouts.status.processing", icon: Loader2, cls: "bg-amber-500/10 text-amber-700 ring-amber-500/20" },
  completed: { key: "merchantPayouts.status.completed", icon: CheckCircle2, cls: "bg-emerald-500/10 text-emerald-700 ring-emerald-500/20" },
  failed: { key: "merchantPayouts.status.failed", icon: XCircle, cls: "bg-rose-500/10 text-rose-700 ring-rose-500/20" },
};

type Payout = { id: string; date: string; amount: number; method: string; status: PStatus; ref: string };

const initialHistory: Payout[] = [
  { id: "PO-2026-0042", date: "May 18, 2026", amount: 2840, method: "Bank · •• 6721", status: "completed", ref: "TRX-948213" },
  { id: "PO-2026-0041", date: "May 11, 2026", amount: 1920, method: "Bank · •• 6721", status: "completed", ref: "TRX-948112" },
  { id: "PO-2026-0040", date: "May 4, 2026", amount: 3210, method: "Wallet · PayPal", status: "processing", ref: "TRX-948022" },
  { id: "PO-2026-0039", date: "Apr 27, 2026", amount: 1540, method: "Bank · •• 6721", status: "completed", ref: "TRX-947814" },
  { id: "PO-2026-0038", date: "Apr 20, 2026", amount: 980, method: "Wallet · PayPal", status: "failed", ref: "TRX-947690" },
  { id: "PO-2026-0037", date: "Apr 13, 2026", amount: 2210, method: "Bank · •• 6721", status: "completed", ref: "TRX-947511" },
];

const methods = [
  { id: "bank", labelKey: "merchantPayouts.method.bankAccount", sub: "Berliner Sparkasse · DE89 •••• 6721", icon: Building2, primary: true },
];

const ACCENT = "#5958F3";

function BalanceCard({
  icon: Icon,
  label,
  value,
  sub,
  tone,
  cta,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
  sub: string;
  tone: "primary" | "amber" | "muted";
  cta?: React.ReactNode;
}) {
  const toneCls =
    tone === "primary"
      ? "border-[#5958F3]/30 bg-gradient-to-br from-[#5958F3]/12 via-background to-background"
      : tone === "amber"
        ? "bg-gradient-to-br from-amber-500/10 via-background to-background"
        : "";
  const iconCls =
    tone === "primary"
      ? "bg-[#5958F3] text-white"
      : tone === "amber"
        ? "bg-amber-500/15 text-amber-600"
        : "bg-[#5958F3]/10 text-[#5958F3]";
  return (
    <Card className={cn("rounded-2xl border-border/60 p-5 shadow-soft transition hover:shadow-md", toneCls)}>
      <div className="flex items-start justify-between">
        <span className={cn("grid h-10 w-10 place-items-center rounded-xl", iconCls)}>
          <Icon className="h-5 w-5" />
        </span>
        {cta}
      </div>
      <p className="mt-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-3xl font-bold tracking-tight">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{sub}</p>
    </Card>
  );
}

function PayoutsPage() {
  const { t } = useTranslation();
  const statusCfg: Record<PStatus, { label: string; icon: React.ComponentType<{ className?: string }>; cls: string }> = {
    processing: { label: t(statusKeys.processing.key), icon: statusKeys.processing.icon, cls: statusKeys.processing.cls },
    completed: { label: t(statusKeys.completed.key), icon: statusKeys.completed.icon, cls: statusKeys.completed.cls },
    failed: { label: t(statusKeys.failed.key), icon: statusKeys.failed.icon, cls: statusKeys.failed.cls },
  };
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("bank");
  const [history, setHistory] = useState<Payout[]>(initialHistory);
  const [statusFilter, setStatusFilter] = useState<"all" | PStatus>("all");
  const [query, setQuery] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detailsFor, setDetailsFor] = useState<Payout | null>(null);
  const [autoPayouts, setAutoPayouts] = useState(false);

  const available = 8420.5;
  const numAmount = Number(amount) || 0;
  const fee = numAmount * 0.01;
  const net = numAmount - fee;
  const canWithdraw = numAmount > 0 && numAmount <= available;

  const filtered = useMemo(() => {
    return history.filter((h) => {
      if (statusFilter !== "all" && h.status !== statusFilter) return false;
      if (query && !`${h.id} ${h.ref} ${h.method}`.toLowerCase().includes(query.toLowerCase())) return false;
      return true;
    });
  }, [history, statusFilter, query]);

  const openConfirm = () => {
    if (!canWithdraw) {
      toast.error(numAmount === 0 ? t("merchantPayouts.toast.enterAmount") : t("merchantPayouts.toast.exceedsBalance"));
      return;
    }
    setConfirmOpen(true);
  };

  const submitWithdraw = () => {
    const newRow: Payout = {
      id: `PO-2026-${String(4043 + history.length).padStart(4, "0")}`,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      amount: numAmount,
      method: method === "bank" ? "Bank · •• 6721" : "Wallet · PayPal",
      status: "processing",
      ref: `TRX-${Math.floor(Math.random() * 900000 + 100000)}`,
    };
    setHistory([newRow, ...history]);
    setAmount("");
    setConfirmOpen(false);
    toast.success(t("merchantPayouts.toast.requested"), { description: t("merchantPayouts.toast.requestedDesc", { id: newRow.id, amount: numAmount.toFixed(2) }) });
  };

  const exportCSV = () => {
    const rows = [
      ["Reference", "Ref", "Date", "Method", "Amount", "Status"],
      ...filtered.map((h) => [h.id, h.ref, h.date, h.method, h.amount.toString(), h.status]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payouts-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t("merchantPayouts.toast.exported", { count: filtered.length }));
  };

  const counts = {
    all: history.length,
    processing: history.filter((h) => h.status === "processing").length,
    completed: history.filter((h) => h.status === "completed").length,
    failed: history.filter((h) => h.status === "failed").length,
  };

  return (
    <MerchantShell
      title={t("merchantPayouts.title")}
      subtitle={t("merchantPayouts.subtitle")}
      actions={
        <Button
          onClick={openConfirm}
          className="h-10 rounded-xl shadow-soft"
          style={{ backgroundColor: ACCENT, color: "white" }}
        >
          <ArrowDownToLine className="h-4 w-4" /> {t("merchantPayouts.withdrawNow")}
        </Button>
      }
    >
      {/* Balances */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <BalanceCard
          icon={Wallet}
          label={t("merchantPayouts.availableBalance")}
          value={<Money value={available.toLocaleString(undefined, { minimumFractionDigits: 2 })} />}
          sub={t("merchantPayouts.readyToWithdraw")}
          tone="primary"
          cta={<Badge className="rounded-full bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">{t("merchantPayouts.live")}</Badge>}
        />
        <BalanceCard
          icon={Clock}
          label={t("merchantPayouts.pendingBalance")}
          value={<Money value="3,210.00" />}
          sub={t("merchantPayouts.clearsIn")}
          tone="amber"
        />
        <BalanceCard
          icon={CheckCircle2}
          label={t("merchantPayouts.totalWithdrawn")}
          value={<Money value="86,394.20" />}
          sub={t("merchantPayouts.acrossPayouts", { count: history.length + 36 })}
          tone="muted"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Withdrawal form */}
        <Card className="min-w-0 rounded-2xl border-border/60 p-4 shadow-soft sm:p-6 lg:col-span-3">
          <div className="flex items-center gap-2.5">
            <span
              className="grid h-9 w-9 place-items-center rounded-xl text-white"
              style={{ backgroundColor: ACCENT }}
            >
              <ArrowDownToLine className="h-4 w-4" />
            </span>
            <div>
              <p className="font-display text-base font-semibold">{t("merchantPayouts.requestWithdrawal")}</p>
              <p className="text-xs text-muted-foreground">{t("merchantPayouts.fundsArrive")}</p>
            </div>
          </div>

          <div className="mt-6 space-y-5">
            <div>
              <Label htmlFor="amount" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("merchantPayouts.amount")}
              </Label>
              <div className="mt-2 flex items-center gap-2">
                <div className="relative flex-1">
                  <span className="pointer-events-none absolute start-4 top-1/2 -translate-y-1/2 text-lg font-semibold text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="amount"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                    className="h-14 rounded-xl border-border/70 ps-9 font-display text-2xl font-bold focus-visible:ring-[#5958F3]/40"
                  />
                </div>
                <Button
                  variant="outline"
                  className="h-14 rounded-xl px-4"
                  onClick={() => setAmount(available.toFixed(2))}
                >
                  {t("merchantPayouts.max")}
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[100, 500, 1000, 2500].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAmount(String(v))}
                    className={cn(
                      "rounded-lg border px-2.5 py-1 text-xs font-medium transition",
                      Number(amount) === v
                        ? "border-[#5958F3] bg-[#5958F3]/10 text-[#5958F3]"
                        : "border-border/60 hover:bg-surface",
                    )}
                  ><Riyal /> {v.toLocaleString()}
                  </button>
                ))}
                <span className="ms-auto text-xs text-muted-foreground">
                  {t("merchantPayouts.available")}: <span className="font-semibold text-foreground"><Riyal /> {available.toLocaleString()}</span>
                </span>
              </div>
              {numAmount > available && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-rose-600">
                  <AlertCircle className="h-3.5 w-3.5" /> {t("merchantPayouts.exceedsBalance")}
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t("merchantPayouts.paymentMethod")}
              </Label>
              <RadioGroup value={method} onValueChange={setMethod} className="mt-2 grid gap-2">
                {methods.map((m) => (
                  <label
                    key={m.id}
                    htmlFor={`pm-${m.id}`}
                    className={cn(
                      "flex w-full min-w-0 cursor-pointer items-center gap-3 rounded-xl border p-3 transition",
                      method === m.id
                        ? "border-[#5958F3]/60 bg-[#5958F3]/5 ring-1 ring-[#5958F3]/30"
                        : "border-border/60 hover:bg-surface",
                    )}
                  >
                    <RadioGroupItem id={`pm-${m.id}`} value={m.id} className="shrink-0" />
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface text-foreground">
                      <m.icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{t(m.labelKey)}</p>
                      <p className="truncate text-xs text-muted-foreground">{m.sub}</p>
                    </div>
                    {m.primary && (
                      <Badge variant="secondary" className="shrink-0 rounded-full text-[10px]">
                        {t("merchantPayouts.primary")}
                      </Badge>
                    )}
                  </label>
                ))}
              </RadioGroup>
            </div>

            {method === "bank" && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="bank-name" className="text-xs text-muted-foreground">
                    {t("merchantPayouts.accountHolder")}
                  </Label>
                  <Input id="bank-name" defaultValue="Central Coffee Co." className="mt-1.5 h-10 rounded-xl" />
                </div>
                <div>
                  <Label htmlFor="iban" className="text-xs text-muted-foreground">
                    {t("merchantPayouts.iban")}
                  </Label>
                  <Input
                    id="iban"
                    defaultValue="DE89 3704 0044 0532 0130 00"
                    className="mt-1.5 h-10 rounded-xl font-mono text-xs"
                  />
                </div>
              </div>
            )}

            <div className="rounded-xl border border-border/60 bg-surface p-3 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">{t("merchantPayouts.withdrawalAmount")}</span>
                <span className="font-semibold tabular-nums"><Riyal /> {numAmount.toFixed(2)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-muted-foreground">{t("merchantPayouts.processingFee")}</span>
                <span className="font-semibold tabular-nums">-${fee.toFixed(2)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-border/60 pt-2">
                <span className="font-semibold">{t("merchantPayouts.youllReceive")}</span>
                <span className="font-display text-base font-bold tabular-nums" style={{ color: ACCENT }}><Riyal /> {net.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={openConfirm}
                disabled={!canWithdraw}
                className="h-11 flex-1 rounded-xl shadow-soft sm:flex-initial sm:px-8"
                style={canWithdraw ? { backgroundColor: ACCENT, color: "white" } : undefined}
              >
                <ArrowDownToLine className="h-4 w-4" /> {t("merchantPayouts.confirmWithdrawal")}
              </Button>
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Shield className="h-3.5 w-3.5" /> {t("merchantPayouts.encrypted")}
              </p>
            </div>
          </div>
        </Card>

        {/* Right column: auto-payouts + tips */}
        <div className="space-y-4 lg:col-span-2">
          <Card className="rounded-2xl border-border/60 bg-gradient-to-br from-[#5958F3]/8 via-background to-background p-5 shadow-soft">
            <div className="flex items-start gap-3">
              <span
                className="grid h-9 w-9 place-items-center rounded-xl text-white"
                style={{ backgroundColor: ACCENT }}
              >
                <Sparkles className="h-4 w-4" />
              </span>
              <div className="flex-1">
                <p className="font-display text-sm font-semibold">{t("merchantPayouts.autoPayouts")}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("merchantPayouts.autoPayoutsDesc")}
                </p>
                <Button
                  variant={autoPayouts ? "outline" : "default"}
                  size="sm"
                  className="mt-3 h-9 rounded-xl text-xs"
                  style={!autoPayouts ? { backgroundColor: ACCENT, color: "white" } : undefined}
                  onClick={() => {
                    setAutoPayouts(!autoPayouts);
                    toast.success(autoPayouts ? t("merchantPayouts.toast.autoDisabled") : t("merchantPayouts.toast.autoEnabled"));
                  }}
                >
                  {autoPayouts ? t("merchantPayouts.disableAuto") : t("merchantPayouts.enableAuto")}
                </Button>
              </div>
            </div>
          </Card>

          <Card className="rounded-2xl border-border/60 p-5 shadow-soft">
            <p className="font-display text-sm font-semibold">{t("merchantPayouts.payoutSchedule")}</p>
            <ul className="mt-3 space-y-2 text-xs text-muted-foreground">
              <li className="flex items-center justify-between">
                <span>{t("merchantPayouts.bankTransfers")}</span>
                <span className="font-semibold text-foreground">{t("merchantPayouts.days1to3")}</span>
              </li>
              <li className="flex items-center justify-between">
                <span>{t("merchantPayouts.walletTransfers")}</span>
                <span className="font-semibold text-foreground">{t("merchantPayouts.within24h")}</span>
              </li>
              <li className="flex items-center justify-between">
                <span>{t("merchantPayouts.processingFee")}</span>
                <span className="font-semibold text-foreground">{t("merchantPayouts.feePercent")}</span>
              </li>
              <li className="flex items-center justify-between">
                <span>{t("merchantPayouts.minWithdrawal")}</span>
                <span className="font-semibold text-foreground"><Riyal /> 50.00</span>
              </li>
            </ul>
          </Card>
        </div>
      </div>

      {/* History */}
      <Card className="mt-6 overflow-hidden rounded-2xl border-border/60 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <div className="flex items-center gap-2.5">
            <span
              className="grid h-9 w-9 place-items-center rounded-xl text-white"
              style={{ backgroundColor: ACCENT }}
            >
              <Clock className="h-4 w-4" />
            </span>
            <div>
              <p className="font-display text-base font-semibold">{t("merchantPayouts.withdrawalHistory")}</p>
              <p className="text-xs text-muted-foreground">
                {t("merchantPayouts.transactionsSummary", { count: filtered.length, total: history.length })}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="pointer-events-none absolute start-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                aria-label={t("merchantPayouts.searchReference")}
                placeholder={t("merchantPayouts.searchReference")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-9 w-52 rounded-xl ps-9 text-xs"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger aria-label={t("merchantPayouts.filters.status", "Status")} className="h-9 w-36 rounded-xl text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("merchantPayouts.filter.all")} ({counts.all})</SelectItem>
                <SelectItem value="processing">{t("merchantPayouts.status.processing")} ({counts.processing})</SelectItem>
                <SelectItem value="completed">{t("merchantPayouts.status.completed")} ({counts.completed})</SelectItem>
                <SelectItem value="failed">{t("merchantPayouts.status.failed")} ({counts.failed})</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-9 rounded-xl text-xs" onClick={exportCSV}>
              <Download className="h-3.5 w-3.5" /> {t("merchantPayouts.exportCsv")}
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto border-t border-border/60">
          <table className="w-full text-sm">
            <thead className="bg-surface text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3 text-start">{t("merchantPayouts.table.reference")}</th>
                <th className="p-3 text-start">{t("merchantPayouts.table.date")}</th>
                <th className="p-3 text-start">{t("merchantPayouts.table.method")}</th>
                <th className="p-3 text-end">{t("merchantPayouts.table.amount")}</th>
                <th className="p-3 text-start">{t("merchantPayouts.table.status")}</th>
                <th className="w-10 p-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-xs text-muted-foreground">
                    {t("merchantPayouts.noMatch")}
                  </td>
                </tr>
              )}
              {filtered.map((h) => {
                const cfg = statusCfg[h.status];
                const StatusIcon = cfg.icon;
                return (
                  <tr
                    key={h.id}
                    onClick={() => setDetailsFor(h)}
                    className="cursor-pointer border-t border-border/60 hover:bg-surface/60"
                  >
                    <td className="p-3">
                      <p className="font-mono text-xs font-semibold">{h.id}</p>
                      <p className="font-mono text-[10px] text-muted-foreground">{h.ref}</p>
                    </td>
                    <td className="p-3">{h.date}</td>
                    <td className="p-3 text-muted-foreground">{h.method}</td>
                    <td className="p-3 text-end font-semibold tabular-nums"><Riyal /> {h.amount.toLocaleString()}</td>
                    <td className="p-3">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset",
                          cfg.cls,
                        )}
                      >
                        <StatusIcon className={cn("h-3 w-3", h.status === "processing" && "animate-spin")} />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="p-3">
                      <Button size="icon" variant="ghost" className="h-8 w-8" aria-label={t("merchantPayouts.details")}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {history.some((h) => h.status === "failed") && (
          <div className="flex items-center gap-2 border-t border-border/60 bg-rose-500/5 px-5 py-3 text-xs text-rose-700">
            <AlertCircle className="h-4 w-4" />
            {t("merchantPayouts.oneFailedNotice")}
          </div>
        )}
      </Card>

      {/* Confirm dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("merchantPayouts.confirmWithdrawal")}</DialogTitle>
            <DialogDescription>{t("merchantPayouts.reviewDetails")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 rounded-xl border border-border/60 bg-surface p-4 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantPayouts.amount")}</span><span className="font-semibold tabular-nums"><Riyal /> {numAmount.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantPayouts.feeOnePercent")}</span><span className="font-semibold tabular-nums">-${fee.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantPayouts.table.method")}</span><span className="font-semibold">{method === "bank" ? t("merchantPayouts.method.bankShort") : t("merchantPayouts.method.walletShort")}</span></div>
            <div className="flex justify-between border-t border-border/60 pt-2"><span className="font-semibold">{t("merchantPayouts.youllReceive")}</span><span className="font-display text-base font-bold tabular-nums" style={{ color: ACCENT }}><Riyal /> {net.toFixed(2)}</span></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="rounded-xl">{t("merchantPayouts.cancel")}</Button>
            <Button onClick={submitWithdraw} className="rounded-xl" style={{ backgroundColor: ACCENT, color: "white" }}>
              {t("merchantPayouts.confirmWithdrawal")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details dialog */}
      <Dialog open={!!detailsFor} onOpenChange={(o) => !o && setDetailsFor(null)}>
        <DialogContent className="rounded-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("merchantPayouts.payoutDetails")}</DialogTitle>
            <DialogDescription>{detailsFor?.id}</DialogDescription>
          </DialogHeader>
          {detailsFor && (
            <div className="space-y-2 rounded-xl border border-border/60 bg-surface p-4 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantPayouts.table.reference")}</span><span className="font-mono text-xs font-semibold">{detailsFor.ref}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantPayouts.table.date")}</span><span className="font-semibold">{detailsFor.date}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantPayouts.table.method")}</span><span className="font-semibold">{detailsFor.method}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantPayouts.table.amount")}</span><span className="font-semibold tabular-nums"><Riyal /> {detailsFor.amount.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("merchantPayouts.table.status")}</span><span className="font-semibold capitalize">{detailsFor.status}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsFor(null)} className="rounded-xl">{t("merchantPayouts.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MerchantShell>
  );
}
